import { useQuery } from "@tanstack/react-query";
import { isMock, getJson } from "./client";
import type { AlertLevel, HealthStatus } from "./types";
import {
  countOpen,
  driftVerdict,
  overallHealth,
  type BandStatus,
  type DecayVerdict,
} from "../classify";

export type SignalLevel =
  | AlertLevel
  | "NOT_MONITORED"
  | "TRACKING"
  | "SEASONING"
  | "PENDING";

export interface HealthSignal {
  label: string;
  level: SignalLevel;
  detail: string;
}

export interface HealthSummary {
  model: {
    cohortsMonitored: number;
    lastRunCohort: string;
  };
  drift: {
    verdict: "DRIFTING" | "STABLE" | "MIXED";
    headline: string;
    signals: HealthSignal[];
    assetTypePsiTrend: { cohort: string; psi: number }[];
  };
  decay: {
    verdict: "TRACKING" | "SEASONING" | "DECAYING";
    headline: string;
    signals: HealthSignal[];
    seasoningSparkline: { mob: number; predicted: number; actual: number }[];
  };
  summary: {
    overallHealth: HealthStatus;
    openCritical: number;
    openWarn: number;
  };
  whatThisMeans: string;
}

const ASSET_PSI: [string, number][] = [
  ["2024-02", 0.592], ["2024-03", 0.557], ["2024-04", 0.519],
  ["2024-05", 0.663], ["2024-06", 0.663], ["2024-07", 0.551],
  ["2024-08", 0.676], ["2024-09", 0.626], ["2024-10", 0.742],
  ["2024-11", 0.724], ["2024-12", 0.660], ["2025-01", 0.629],
  ["2025-02", 0.627], ["2025-03", 0.655], ["2025-04", 0.780],
  ["2025-05", 0.868], ["2025-06", 1.308],
];

const SEASONING: [number, number, number][] = [
  [0, 0.2, 0.1],
  [6, 1.1, 0.7],
  [12, 2.2, 1.8],
  [18, 3.1, 2.9],
  [24, 3.8, 3.9],
  [30, 4.2, 4.5],
  [36, 4.5, 4.9],
];

const DRIFT_SIGNALS: HealthSignal[] = [
  {
    label: "Asset Type",
    level: "CRITICAL",
    detail: "PSI 1.31, escalating across 17 cohorts",
  },
  {
    label: "Credit inputs",
    level: "STABLE",
    detail: "FICO, PayNet within training range",
  },
  {
    label: "Macro inputs",
    level: "CRITICAL",
    detail: "Fed Funds, CPI, Unemployment, Industrial Production drifting (PSI ≥ 0.25)",
  },
];

const DECAY_SIGNALS: HealthSignal[] = [
  {
    label: "Mature vintages (2018–2022)",
    level: "TRACKING",
    detail: "predicted vs actual default aligned",
  },
  {
    label: "Recent vintages (2024–2025)",
    level: "SEASONING",
    detail: "outcomes still accumulating",
  },
  {
    label: "Discrimination (AUC)",
    level: "PENDING",
    detail: "computed per vintage at maturity",
  },
];

// Verdicts + counts derived from the signals so mock stays self-consistent
// (live mode gets these computed server-side by /health).
const DRIFT_BAND_LEVELS: BandStatus[] = DRIFT_SIGNALS.map((s) => s.level).filter(
  (l): l is AlertLevel => l === "CRITICAL" || l === "WARN" || l === "STABLE",
);
const DRIFT_VERDICT = driftVerdict(DRIFT_BAND_LEVELS);
const DECAY_VERDICT: DecayVerdict = "SEASONING";
const { openCritical, openWarn } = countOpen(DRIFT_BAND_LEVELS);

const MOCK_HEALTH: HealthSummary = {
  model: {
    cohortsMonitored: 17,
    lastRunCohort: "2025-06",
  },
  drift: {
    verdict: DRIFT_VERDICT,
    headline:
      "The mix of equipment types and the macro environment have both shifted sharply from the model's training period. Credit inputs are mostly stable.",
    signals: DRIFT_SIGNALS,
    assetTypePsiTrend: ASSET_PSI.map(([cohort, psi]) => ({ cohort, psi })),
  },
  decay: {
    verdict: DECAY_VERDICT,
    headline:
      "Recent loans are too young to judge. On mature vintages, predictions track actual defaults closely.",
    signals: DECAY_SIGNALS,
    seasoningSparkline: SEASONING.map(([mob, predicted, actual]) => ({
      mob,
      predicted,
      actual,
    })),
  },
  summary: {
    overallHealth: overallHealth(DRIFT_VERDICT, DECAY_VERDICT, openCritical, openWarn),
    openCritical,
    openWarn,
  },
  whatThisMeans:
    "Athena's monitoring shows the dominant drift signals are the equipment-type mix and the macro environment, both of which have shifted significantly from the 2018–2023 training period and are escalating. The model's credit inputs remain stable. On whether the model's judgment is still accurate: the loans old enough to judge show predictions tracking actual defaults well; more recent loans are still too young to produce a verdict. Open Input Stability to see what is moving, Observability to see whether predictions are coming true, and Risk Appetite to set and sign off the approval threshold.",
};

export async function fetchHealthSummary(modelId: number): Promise<HealthSummary> {
  if (isMock) return Promise.resolve(MOCK_HEALTH);
  return getJson<HealthSummary>(`model-drift-monitoring/health?model_id=${modelId}`);
}

export function useHealthSummary(modelId: number) {
  return useQuery({
    queryKey: ["health-summary", modelId],
    queryFn: () => fetchHealthSummary(modelId),
    enabled: modelId > 0,
  });
}

export function levelClasses(level: SignalLevel | string): string {
  switch (level) {
    case "CRITICAL":
    case "DECAYING":
      return "bg-[color:var(--decline)]/15 text-[color:var(--decline)] border-[color:var(--decline)]/40";
    case "STABLE":
    case "TRACKING":
      return "bg-[color:var(--approve)]/15 text-[color:var(--approve)] border-[color:var(--approve)]/40";
    case "WARN":
    case "SEASONING":
    case "AMBER":
      return "bg-amber-dim text-amber border-amber/40";
    case "RED":
      return "bg-[color:var(--decline)]/15 text-[color:var(--decline)] border-[color:var(--decline)]/40";
    case "GREEN":
      return "bg-[color:var(--approve)]/15 text-[color:var(--approve)] border-[color:var(--approve)]/40";
    case "DRIFTING":
      return "bg-[color:var(--decline)]/15 text-[color:var(--decline)] border-[color:var(--decline)]/40";
    case "NOT_MONITORED":
    case "PENDING":
    default:
      return "bg-muted/40 text-muted-foreground border-border";
  }
}
